/**
 * Clínicas — Page where Profesor / Directivo / Admin schedule clinics & classes,
 * assigning a court from the club, professor, modality, focus and capacity.
 */
import { useState, useEffect, useMemo, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SearchableUserSelect from "@/components/SearchableUserSelect";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, GraduationCap, Trash, PencilSimple, CheckCircle, X, ShareNetwork } from "@phosphor-icons/react";

const EMPTY = {
  club_id: "", court_name: "", kind: "clinica", modality: "grupal", focus: "tecnico",
  professor_id: "", professor_name: "",
  date: new Date().toISOString().slice(0, 10), time: "",
  duration_min: 60, capacity: 8, price: 0, description: "",
  warmup_min: 5, baskets_count: 0, balls_count: 0, ball_type: "presurizadas",
};

const KIND = [
  { value: "clinica", label: "Clínica" },
  { value: "clase",   label: "Clase" },
];
const MODALITY = [
  { value: "grupal",   label: "Grupal" },
  { value: "personal", label: "Personal" },
  { value: "dupla",    label: "Dupla" },
];
const FOCUS = [
  { value: "fisico",   label: "Físico" },
  { value: "tecnico",  label: "Técnico" },
  { value: "mecanico", label: "Mecánico" },
  { value: "jugada",   label: "Jugada" },
];
const BALL_TYPE = [
  { value: "no_presurizadas", label: "No presurizadas" },
  { value: "presurizadas",    label: "Presurizadas" },
  { value: "con_peso",        label: "Con peso" },
];

export default function Clinics() {
  const { user } = useAuth();
  const isStaff = user && ["admin", "directivo", "profesor"].includes(user.role);

  const [items, setItems] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [professors, setProfessors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // { mode: "create"|"edit", clinic? }
  const [form, setForm] = useState(EMPTY);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // all | abierta | cerrada | completada

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get("/clinics"),
      api.get("/clubs"),
      api.get("/users?role=profesor").catch(() => ({ data: [] })),
    ]).then(([c, k, p]) => {
      setItems(c.data || []);
      setClubs(k.data || []);
      setProfessors(p.data || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((c) => {
      if (filter !== "all" && c.status !== filter) return false;
      if (!q) return true;
      return [c.professor_name, c.club_name, c.court_name, c.kind, c.modality, c.focus, c.description, c.date]
        .filter(Boolean).join(" ").toLowerCase().includes(q);
    });
  }, [items, search, filter]);

  const openCreate = () => {
    setForm({
      ...EMPTY,
      club_id: user?.club_id || "",
      professor_id: user?.role === "profesor" ? user.id : "",
      professor_name: user?.role === "profesor" ? user.name : "",
    });
    setDialog({ mode: "create" });
  };

  const openEdit = (c) => {
    setForm({
      club_id: c.club_id || "",
      court_name: c.court_name || "",
      kind: c.kind || "clinica",
      modality: c.modality || "grupal",
      focus: c.focus || "tecnico",
      professor_id: c.professor_id || "",
      professor_name: c.professor_name || "",
      date: c.date || "",
      time: c.time || "",
      duration_min: c.duration_min || 60,
      capacity: c.capacity || 8,
      price: c.price || 0,
      description: c.description || "",
      warmup_min: c.warmup_min || 5,
      baskets_count: c.baskets_count || 0,
      balls_count: c.balls_count || 0,
      ball_type: c.ball_type || "presurizadas",
    });
    setDialog({ mode: "edit", clinic: c });
  };

  const save = async () => {
    try {
      if (!form.club_id || !form.court_name || !form.date) {
        toast.error("Club, cancha y fecha son obligatorios");
        return;
      }
      const prof = professors.find((p) => p.id === form.professor_id);
      const payload = {
        ...form,
        professor_name: prof?.name || form.professor_name || null,
        duration_min: parseInt(form.duration_min) || 60,
        capacity: parseInt(form.capacity) || 8,
        price: parseFloat(form.price) || 0,
        warmup_min: parseInt(form.warmup_min) || 0,
        baskets_count: parseInt(form.baskets_count) || 0,
        balls_count: parseInt(form.balls_count) || 0,
      };
      if (dialog.mode === "edit") {
        await api.put(`/clinics/${dialog.clinic.id}`, payload);
        toast.success("Clínica actualizada");
      } else {
        await api.post("/clinics", payload);
        toast.success("Clínica creada");
      }
      setDialog(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    }
  };

  const enroll = async (c) => {
    try { await api.post(`/clinics/${c.id}/enroll`); toast.success("Inscrito"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const unenroll = async (c) => {
    try { await api.post(`/clinics/${c.id}/unenroll`); toast.success("Desinscrito"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const removeClinic = async (c) => {
    if (!window.confirm(`Eliminar clínica ${c.kind} del ${c.date}?`)) return;
    try { await api.delete(`/clinics/${c.id}`); toast.success("Eliminada"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const shareClinic = async (c) => {
    const kindLabel = c.kind === "clase" ? "Clase" : "Clínica";
    const url = `${window.location.origin}/dashboard/clinicas`;
    const time = c.time ? ` ${c.time}` : "";
    const price = c.price > 0 ? ` · $${c.price}` : " · GRATIS";
    const text = `${kindLabel}: ${c.professor_name || "Profesor"} · ${c.court_name} (${c.club_name})\n${c.date}${time} · ${c.duration_min} min${price}\nInscríbete en Disfruta y Diviértete`;
    try {
      if (navigator.share) {
        await navigator.share({ title: `${kindLabel} · ${c.professor_name || ""}`, text, url });
      } else {
        await navigator.clipboard.writeText(`${text}\n${url}`);
        toast.success("Clínica copiada al portapapeles");
      }
    } catch { /* dismissed */ }
  };

  const courtNamesForForm = useMemo(() => {
    const cb = clubs.find((c) => c.id === form.club_id);
    return cb?.court_names || [];
  }, [clubs, form.club_id]);

  return (
    <div className="space-y-6" data-testid="clinics-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#CCFF00] font-bold mb-1">Clínicas y Clases</p>
          <h1 className="font-['Barlow_Condensed'] text-3xl sm:text-4xl font-black uppercase tracking-tight">Entrenamiento Programado</h1>
          <p className="text-sm text-gray-400 mt-1">Profesor y Directivo asignan cancha del club, modalidad, enfoque y cupo.</p>
        </div>
        {isStaff && (
          <Button onClick={openCreate} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase" data-testid="clinics-new-btn">
            <Plus size={16} className="mr-1" /> Nueva clínica
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="card-tactical p-4 flex items-center gap-2 flex-wrap" data-testid="clinics-filters">
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar por profesor, club, cancha…" className="bg-[#0A0A0A] border-white/10 max-w-md h-10" data-testid="clinics-search" />
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="bg-[#0A0A0A] border-white/10 h-10 w-44" data-testid="clinics-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            <SelectItem value="abierta">Abiertas</SelectItem>
            <SelectItem value="cerrada">Cerradas</SelectItem>
            <SelectItem value="completada">Completadas</SelectItem>
            <SelectItem value="cancelada">Canceladas</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-[10px] uppercase tracking-widest text-gray-500 ml-auto">{filtered.length} de {items.length}</span>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center h-32"><div className="w-6 h-6 border-2 border-[#CCFF00] border-t-transparent animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="card-tactical p-12 text-center" data-testid="clinics-empty">
          <GraduationCap size={48} weight="duotone" className="mx-auto text-[#CCFF00]/40" />
          <p className="text-gray-400 mt-4">Aún no hay clínicas registradas.</p>
          {isStaff && <p className="text-xs text-gray-500 mt-2">Crea la primera con el botón superior.</p>}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((c) => {
            const enrolled = c.enrolled || [];
            const isEnrolled = enrolled.some((e) => e.user_id === user?.id);
            const canEdit = user?.role === "admin"
              || (user?.role === "directivo" && user.club_id === c.club_id)
              || (user?.role === "profesor" && (c.professor_id === user.id || c.created_by === user.id));
            const kindLabel = KIND.find((k) => k.value === c.kind)?.label || c.kind;
            const focusLabel = FOCUS.find((k) => k.value === c.focus)?.label || c.focus;
            const modalityLabel = MODALITY.find((k) => k.value === c.modality)?.label || c.modality;
            return (
              <div key={c.id} className="card-tactical p-4 flex flex-col gap-3" data-testid={`clinic-card-${c.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <Badge variant="outline" className="bg-[#CCFF00]/10 text-[#CCFF00] border-[#CCFF00]/30 text-[10px] uppercase">{kindLabel}</Badge>
                      <Badge variant="outline" className="bg-blue-500/10 text-blue-300 border-blue-500/30 text-[10px] uppercase">{modalityLabel}</Badge>
                      <Badge variant="outline" className="bg-orange-500/10 text-orange-300 border-orange-500/30 text-[10px] uppercase">{focusLabel}</Badge>
                    </div>
                    <h3 className="font-['Barlow_Condensed'] text-xl font-black uppercase truncate" data-testid={`clinic-title-${c.id}`}>
                      {c.professor_name || "Profesor"} · {c.court_name}
                    </h3>
                    <p className="text-xs text-gray-500 truncate">{c.club_name} · {c.date}{c.time ? ` ${c.time}` : ""} · {c.duration_min} min</p>
                  </div>
                  {canEdit && (
                    <div className="flex flex-col gap-1 shrink-0">
                      <button onClick={() => shareClinic(c)} className="p-1.5 text-gray-400 hover:text-[#CCFF00] border border-white/10 hover:border-[#CCFF00]/30" data-testid={`clinic-share-${c.id}`} title="Compartir"><ShareNetwork size={12} /></button>
                      <button onClick={() => openEdit(c)} className="p-1.5 text-gray-400 hover:text-[#CCFF00] border border-white/10 hover:border-[#CCFF00]/30" data-testid={`clinic-edit-${c.id}`}><PencilSimple size={12} /></button>
                      <button onClick={() => removeClinic(c)} className="p-1.5 text-gray-400 hover:text-red-400 border border-white/10 hover:border-red-500/30" data-testid={`clinic-delete-${c.id}`}><Trash size={12} /></button>
                    </div>
                  )}
                  {!canEdit && (
                    <button onClick={() => shareClinic(c)} className="p-1.5 text-gray-400 hover:text-[#CCFF00] border border-white/10 hover:border-[#CCFF00]/30 shrink-0" data-testid={`clinic-share-${c.id}`} title="Compartir"><ShareNetwork size={12} /></button>
                  )}
                </div>
                {c.description && <p className="text-xs text-gray-400">{c.description}</p>}
                <div className="grid grid-cols-3 gap-2 text-center border-t border-white/5 pt-2">
                  <div><p className="text-[9px] uppercase tracking-widest text-gray-500">Cupo</p><p className="font-['Barlow_Condensed'] font-black text-lg">{enrolled.length}/{c.capacity}</p></div>
                  <div><p className="text-[9px] uppercase tracking-widest text-gray-500">Precio</p><p className="font-['Barlow_Condensed'] font-black text-lg">{c.price > 0 ? `$${c.price}` : "GRATIS"}</p></div>
                  <div><p className="text-[9px] uppercase tracking-widest text-gray-500">Estado</p><p className="font-['Barlow_Condensed'] font-black text-lg text-[#CCFF00]">{c.status}</p></div>
                </div>
                {user?.role === "jugador" && c.status === "abierta" && (
                  isEnrolled
                    ? <Button onClick={() => unenroll(c)} variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10 uppercase text-xs h-8" data-testid={`clinic-unenroll-${c.id}`}><X size={12} className="mr-1" /> Desinscribirme</Button>
                    : (enrolled.length < c.capacity
                      ? <Button onClick={() => enroll(c)} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase text-xs h-8" data-testid={`clinic-enroll-${c.id}`}><CheckCircle size={12} className="mr-1" /> Inscribirme</Button>
                      : <p className="text-xs text-red-400 text-center font-bold uppercase">Clínica llena</p>)
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="bg-[#141414] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="clinic-form-dialog">
          <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-xl font-bold uppercase">{dialog?.mode === "edit" ? "Editar clínica" : "Nueva clínica"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-widest">Club</Label>
                <Select value={form.club_id} onValueChange={(v) => setForm({ ...form, club_id: v, court_name: "" })}>
                  <SelectTrigger className="bg-[#0A0A0A] border-white/10" data-testid="clinic-form-club"><SelectValue placeholder="Selecciona club" /></SelectTrigger>
                  <SelectContent className="max-h-[260px]">
                    {clubs.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest">Cancha</Label>
                <Select value={form.court_name || "__none"} onValueChange={(v) => setForm({ ...form, court_name: v === "__none" ? "" : v })} disabled={!form.club_id}>
                  <SelectTrigger className="bg-[#0A0A0A] border-white/10" data-testid="clinic-form-court">
                    <SelectValue placeholder={!form.club_id ? "Elige club" : courtNamesForForm.length ? "Selecciona cancha" : "Sin canchas"} />
                  </SelectTrigger>
                  <SelectContent className="max-h-[260px]">
                    <SelectItem value="__none">Sin asignar</SelectItem>
                    {courtNamesForForm.map((cn) => <SelectItem key={cn} value={cn}>{cn}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-widest">Tipo</Label>
                <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                  <SelectTrigger className="bg-[#0A0A0A] border-white/10" data-testid="clinic-form-kind"><SelectValue /></SelectTrigger>
                  <SelectContent>{KIND.map((k) => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest">Modalidad</Label>
                <Select value={form.modality} onValueChange={(v) => setForm({ ...form, modality: v })}>
                  <SelectTrigger className="bg-[#0A0A0A] border-white/10" data-testid="clinic-form-modality"><SelectValue /></SelectTrigger>
                  <SelectContent>{MODALITY.map((k) => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest">Enfoque</Label>
                <Select value={form.focus} onValueChange={(v) => setForm({ ...form, focus: v })}>
                  <SelectTrigger className="bg-[#0A0A0A] border-white/10" data-testid="clinic-form-focus"><SelectValue /></SelectTrigger>
                  <SelectContent>{FOCUS.map((k) => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-widest">Profesor</Label>
                <SearchableUserSelect
                  value={form.professor_id}
                  onChange={(id, p) => setForm({ ...form, professor_id: id, professor_name: p?.name || "" })}
                  options={professors}
                  placeholder="Selecciona profesor"
                  allowClear
                  testId="clinic-form-professor"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest">Cupo</Label>
                <Input type="number" min="1" max="50" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} className="bg-[#0A0A0A] border-white/10" data-testid="clinic-form-capacity" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-[10px] uppercase tracking-widest">Fecha</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="bg-[#0A0A0A] border-white/10" data-testid="clinic-form-date" /></div>
              <div><Label className="text-[10px] uppercase tracking-widest">Hora</Label><Input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="bg-[#0A0A0A] border-white/10" data-testid="clinic-form-time" /></div>
              <div><Label className="text-[10px] uppercase tracking-widest">Duración (min)</Label><Input type="number" min="15" step="15" value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: e.target.value })} className="bg-[#0A0A0A] border-white/10" data-testid="clinic-form-duration" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-[10px] uppercase tracking-widest">Precio ($)</Label><Input type="number" min="0" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="bg-[#0A0A0A] border-white/10" data-testid="clinic-form-price" /></div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest">Tipo de pelotas</Label>
                <Select value={form.ball_type} onValueChange={(v) => setForm({ ...form, ball_type: v })}>
                  <SelectTrigger className="bg-[#0A0A0A] border-white/10" data-testid="clinic-form-ball-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{BALL_TYPE.map((k) => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-[10px] uppercase tracking-widest">Calentamiento (min)</Label><Input type="number" min="0" value={form.warmup_min} onChange={(e) => setForm({ ...form, warmup_min: e.target.value })} className="bg-[#0A0A0A] border-white/10" data-testid="clinic-form-warmup" /></div>
              <div><Label className="text-[10px] uppercase tracking-widest">Cestos</Label><Input type="number" min="0" value={form.baskets_count} onChange={(e) => setForm({ ...form, baskets_count: e.target.value })} className="bg-[#0A0A0A] border-white/10" data-testid="clinic-form-baskets" /></div>
              <div><Label className="text-[10px] uppercase tracking-widest">Pelotas</Label><Input type="number" min="0" value={form.balls_count} onChange={(e) => setForm({ ...form, balls_count: e.target.value })} className="bg-[#0A0A0A] border-white/10" data-testid="clinic-form-balls" /></div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest">Descripción / Plan</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="bg-[#0A0A0A] border-white/10" data-testid="clinic-form-description" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)} className="border-white/10">Cancelar</Button>
            <Button onClick={save} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase" data-testid="clinic-form-save">
              {dialog?.mode === "edit" ? "Actualizar" : "Crear clínica"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
